import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Parse from '../../../config/parse';
import { generateToken, verifyToken, saveToken, getToken, removeToken } from '../services/tokenService';
import { setupAutoRefresh } from '../services/refreshTokenService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const cleanupRef = useRef(null);
  const navigate = useNavigate();

  // Update last activity timestamp on user interaction
  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());
    
    // Add event listeners for user activity
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    
    return () => {
      // Clean up event listeners
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
    };
  }, []);
  
  // Check for session timeout every minute
  useEffect(() => {
    const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    
    const intervalId = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      
      if (user && timeSinceLastActivity > SESSION_TIMEOUT) {
        console.log('Session timeout due to inactivity');
        logout();
        navigate('/login', { state: { message: 'Your session has expired due to inactivity' } });
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, [user, lastActivity]);
  
  // Verify session on load and set up token refresh
  useEffect(() => {
    const checkUser = async () => {
      try {
        // First try to get user from Parse
        const currentUser = await Parse.User.current();
        if (currentUser) {
          setUser(currentUser);
          // Update JWT token if user is authenticated
          const token = generateToken(currentUser);
          saveToken(token);
          
          // Set up automatic token refresh
          if (cleanupRef.current) {
            cleanupRef.current(); // Clean up previous refresh interval
          }
          cleanupRef.current = setupAutoRefresh();
        } else {
          // If no user in Parse, try to restore from JWT
          const token = getToken();
          if (token) {
            const decodedToken = verifyToken(token);
            if (decodedToken && decodedToken.id) {
              // Try to get user from Parse using token ID
              try {
                const query = new Parse.Query(Parse.User);
                const user = await query.get(decodedToken.id);
                if (user) {
                  setUser(user);
                  
                  // Set up automatic token refresh
                  if (cleanupRef.current) {
                    cleanupRef.current(); // Clean up previous refresh interval
                  }
                  cleanupRef.current = setupAutoRefresh();
                } else {
                  // If user not found, remove token
                  removeToken();
                }
              } catch (parseError) {
                console.error('Error getting user from token:', parseError);
                removeToken();
              }
            } else {
              // Invalid or expired token
              removeToken();
            }
          }
        }
      } catch (error) {
        console.error('Error checking user session:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
    
    // Clean up token refresh interval on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const user = await Parse.User.logIn(email, password);
      
      // Generate and save JWT token
      const token = generateToken(user);
      saveToken(token);
      
      // Set user and determine role
      setUser(user);
      const userType = user.get('type') || 'client';
      const isAdmin = userType === 'admin' || user.get('isAdmin') === true;
      setUserRole(isAdmin ? 'admin' : 'client');
      
      // Reset last activity timestamp
      setLastActivity(Date.now());
      
      return user;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.message);
    }
  }, []);

  const signup = useCallback(async (userData) => {
    try {
      const user = new Parse.User();
      user.set('username', userData.email);
      user.set('email', userData.email);
      user.set('password', userData.password);
      user.set('name', userData.name);
      user.set('phone', userData.phone);
      user.set('type', 'client'); // Always set as client when registering
      
      const result = await user.signUp();
      
      // Generate and save JWT token
      const token = generateToken(result);
      saveToken(token);
      
      // Set user and role
      setUser(result);
      setUserRole('client');
      
      // Reset last activity timestamp
      setLastActivity(Date.now());
      
      return result;
    } catch (error) {
      console.error('Signup error:', error);
      throw new Error(error.message);
    }
  }, []);

  const logout = useCallback(async (redirectTo = '/login') => {
    try {
      await Parse.User.logOut();
      // Remove JWT token on logout
      removeToken();
      setUser(null);
      setUserRole(null);
      
      // Clean up token refresh interval
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      
      // Clear any saved redirect paths
      sessionStorage.removeItem('redirectAfterLogin');
      
      // Redirect after logout if a path is provided
      if (redirectTo && navigate) {
        navigate(redirectTo);
      }
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error(error.message);
    }
  }, [navigate]);

  const resetPassword = async (email) => {
    try {
      await Parse.User.requestPasswordReset(email);
    } catch (error) {
      throw new Error(`Error al restablecer contraseña: ${error.message}`);
    }
  };

  // Get the current JWT token
  const getCurrentToken = useCallback(() => {
    return getToken();
  }, []);
  
  // Get the appropriate home page based on user role
  const getHomePage = useCallback(() => {
    if (!user) return '/login';
    
    if (userRole === 'admin') {
      return '/admin';
    } else {
      return '/dashboard';
    }
  }, [user, userRole]);
  
  // Redirect to the appropriate page based on user role and context
  const redirectToAppropriateRoute = useCallback(async () => {
    if (!user) return false;
    
    let redirectTo = null;
    
    // Check for saved redirect path in sessionStorage
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    if (savedRedirect) {
      redirectTo = savedRedirect;
      sessionStorage.removeItem('redirectAfterLogin');
    } else {
      // Default routes based on user role
      if (userRole === 'admin') {
        // Redirigir administradores al panel de administración
        redirectTo = '/admin';
      } else {
        // Para clientes, redirigir al dashboard de usuario
        redirectTo = '/user/dashboard';
      }
    }
    
    if (redirectTo && navigate) {
      console.log('Redirigiendo a:', redirectTo);
      navigate(redirectTo);
      return true;
    }
    
    return false;
  }, [user, userRole, navigate]);

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    resetPassword,
    getCurrentToken,
    getHomePage,
    redirectToAppropriateRoute,
    userRole,
    isAuthenticated: !!user,
    isAdmin: userRole === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};
